import React, { useMemo } from 'react';
import { useMediaQuery } from '@material-ui/core';
import type Transit from '@this/src/domain/transit/transit';
import type SelectStore from '../../../../../domain/select_store';
import { Box } from '../../shared/box_parts';
import IndividualRouteInfo from '../short_distance/individual/IndividualRouteInfo';
import PackageRouteInfo from '../short_distance/package/PackageRouteInfo';
import SelectTransportBoxRightInListTemplate from '../right/right_in_list.template';
import SelectTransportBoxRightInDomesticAirListTemplate from '../right/right_in_domestic_air_list.template';
import SameDayUnavailableBox from '../same_day_unavailable_box';

interface TransportBoxShortDistanceListContainerProps {
  transit: Transit;
  selected: boolean;
  domesticAirPriceIndex: number;
  loading: boolean;
  store: SelectStore;
  handleDomesticAirPriceChange: (index: number) => void;
  handleChange: () => void;
}

const TransportBoxShortDistanceListContainer: React.FC<TransportBoxShortDistanceListContainerProps> = ({
  transit,
  selected,
  domesticAirPriceIndex,
  loading,
  handleDomesticAirPriceChange,
  handleChange,
  store
}) => {
  const isMobile = useMediaQuery('(max-width:768px)');
  const styles = useMemo(() => getStyles(isMobile), [isMobile]);

  const priceData = transit.pricesOriginal?.[domesticAirPriceIndex];
  const airSegmentSources = useMemo(() => {
    const airSegs = transit.segments.filter(s => s.isAir());
    return Array.from(new Set(airSegs.flatMap(s => s.sources ?? [])));
  }, [transit.segments]);

  const hasAir = transit.airExists();

  const renderIndividualSegments = (segment: Transit['segments'][number], segmentIdx: number) => {
    const changeableLegSummaries = segment.leg_summaries?.filter(
      legSummary => legSummary.type === 'jr' || legSummary.type === 'air'
    );

    if (changeableLegSummaries?.length) {
      return changeableLegSummaries.map((legSummary, j) => (
        <div key={`individual_${segmentIdx}_${j}`} style={styles.segment} className="domestic-transport__segment">
          <IndividualRouteInfo
            legSummary={legSummary}
            segment={segment}
            isTrainOrFlight={legSummary.type === 'jr' || legSummary.type === 'air'}
            omitOuterPadding
          />
        </div>
      ));
    }

    return segment.legs.map((leg, legIndex) => (
      <div
        key={`individual_leg_${segmentIdx}_${legIndex}`}
        style={styles.segment}
        className="domestic-transport__segment"
      >
        <PackageRouteInfo
          leg={leg}
          legIndex={legIndex}
          segment={segment}
          segmentIdx={segmentIdx}
          hasAir={hasAir}
          omitOuterPadding
        />
      </div>
    ));
  };

  const renderRightContent = () => {
    if (transit.package) {
      return (
        <SelectTransportBoxRightInListTemplate
          loading={loading}
          selected={selected}
          hovered={transit.hovered}
          priceRange={store.result.type === 'airPackage' ? transit.price_range ?? undefined : undefined}
          showsPricePerPersonText={store.result.type !== 'airPackage'}
          onClick={handleChange}
          onTouchStart={transit.handleTouched}
          onMouseOver={() => transit.setHovered(true)}
          onMouseLeave={() => transit.setHovered(false)}
          isPackage={transit.package}
          intlSpCandidatePrice={!store.result.isDomesticAirResult()}
        />
      );
    }

    if (transit.airExists()) {
      return (
        <div
          style={{
            width: '100%',
            padding: '5px 10px 10px',
            display: 'flex',
            flexDirection: 'column',
            gap: '5px'
          }}
          className="flight"
        >
          {transit.isSameDayUnselectable() ? (
            <SameDayUnavailableBox />
          ) : (
            transit.prices?.map((p, i) => {
              const isPriceSameDayRestricted = transit.isPriceSameDayRestricted(p);
              return (
                <div
                  key={i}
                  onClick={() => {
                    if (isPriceSameDayRestricted) return;
                    handleDomesticAirPriceChange(i);
                  }}
                  style={isPriceSameDayRestricted ? { cursor: 'default' } : undefined}
                >
                  <SelectTransportBoxRightInDomesticAirListTemplate
                    loading={loading}
                    selected={selected && priceData != null && p === priceData}
                    price={p}
                    flightNumber={transit.firstLegName()}
                    sources={airSegmentSources}
                    disabled={isPriceSameDayRestricted}
                  />
                </div>
              );
            })
          )}
        </div>
      );
    }

    const vacancyMark = transit.shinkansenVacancyStatus
      ? transit.segments.find(s => s.shinkansenVacancyMark())?.shinkansenVacancyMark() ?? null
      : null;
    const isShinkansenUnavailable = transit.segments.some(s => s.isShinkansenUnavailable());
    const hasShinSegment = transit.segments.some(s => s.type === 'shin');

    return (
      <SelectTransportBoxRightInListTemplate
        loading={loading}
        selected={selected}
        hovered={transit.hovered}
        price={transit.price ?? undefined}
        isDomestic={store.result.isDomesticAirResult()}
        seatCount={vacancyMark}
        seatCountLoading={hasShinSegment && transit.shinkansenVacancyLoading}
        disabled={isShinkansenUnavailable}
        onClick={handleChange}
        onTouchStart={transit.handleTouched}
        onMouseOver={() => transit.setHovered(true)}
        onMouseLeave={() => transit.setHovered(false)}
        intlSpCandidatePrice={!store.result.isDomesticAirResult()}
      />
    );
  };

  return (
    <Box className="domestic-transport-box" selected={selected}>
      <div style={styles.left}>
        {transit.segments.map((segment, segmentIdx) =>
          transit.package
            ? segment.legs.map((leg, legIndex) => (
                <div
                  key={`package_${segmentIdx}_${legIndex}`}
                  style={styles.segment}
                  className="domestic-transport__segment"
                >
                  <PackageRouteInfo
                    leg={leg}
                    legIndex={legIndex}
                    segment={segment}
                    segmentIdx={segmentIdx}
                    hasAir={hasAir}
                    omitOuterPadding
                  />
                </div>
              ))
            : renderIndividualSegments(segment, segmentIdx)
        )}
      </div>
      {renderRightContent()}
    </Box>
  );
};

const getStyles = (isMobile: boolean) => ({
  left: {
    flexShrink: 10,
    padding: isMobile ? '10px' : '8px 5px 8px 10px',
    margin: 'auto 0'
  } as const,
  segment: {} as const
});

export default TransportBoxShortDistanceListContainer;
